"use client";
import React, { useEffect, useRef, useState } from "react";

import {
  InputText,
  InputSelect,
  Button,
  DropdownSection,
  DropdownOption,
  Checkbox,
  RadioButton,
  Breadcrumb,
  Icon,
} from "@ama-pt/agora-design-system";
import { useTranslations } from "next-intl";
import { motion, useAnimation, useInView } from "framer-motion";
import { sanitizeEmail, sanitizeInput, sanitizePhone } from "@/lib/utils";

interface FormValues {
  [key: string]: any;
}

export default function RegisterForm({ servicesAvailable }: any) {
  const translate = useTranslations();
  const t = useTranslations("form");

  const [formValues, setFormValues] = useState<FormValues>({});
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");
  const statusMessageRef = useRef<HTMLDivElement | null>(null);

  const getErrorId = (fieldName: string) => `register-${fieldName}-error`;
  const getDescribedBy = (fieldName: string, helperId?: string) =>
    [helperId, formErrors[fieldName] ? getErrorId(fieldName) : ""].filter(Boolean).join(" ") || undefined;

  useEffect(() => {
    const initialValues: FormValues = {
      inscricaoTipo: "",
      entidade: "",
      municipio: "",
      municipiosAderentes: "",
      nomeAdministrador: "",
      nomeCompleto: "",
      emailInstitucional: "",
      telefone: "",
      cartaoCidadao: "",
      habilitacoes: "",
      emailRequerente: "",
      declaracaoRequerente: false,
      declaracaoResponsabilidade: false,
      declaracaoPrivacidade: false,
    };

    setFormValues(initialValues);
  }, []);

  const handleFieldChange = (name: string, value: any) => {
   const actualValue = value?.target ? value.target.checked : value;
  
  setFormValues((prev) => ({
    ...prev,
    [name]: actualValue,
    ...(name === "inscricaoTipo" ? { entidade: "" } : {}),
  }));

  if (actualValue !== false && actualValue) {
    let error = "";
    
    if (name === "emailInstitucional" || name === "emailRequerente") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        error = "Email inválido";
      }
    }
    
    if (name === "telefone") {
      const numbersOnly = value.replace(/\D/g, "");
      if (numbersOnly.length < 9) {
        error = "Telefone deve ter no mínimo 9 dígitos";
      }
      setFormValues((prev) => ({
        ...prev,
        [name]: numbersOnly,
      }));
    }
    
    if (name === "cartaoCidadao") {
      const digitsOnly = value.replace(/[\s-]/g, "");
      if (digitsOnly.length < 11) {
        error = "Cartão de cidadão deve ter no mínimo 11 dígitos";
      }
    }

    setFormErrors((prev) => {
      const newErrors = { ...prev };
      if (name === "inscricaoTipo") {
        delete newErrors.entidade;
      }

      if (error) {
        newErrors[name] = error;
      } else {
        delete newErrors[name];
      }
      return newErrors;
    });
  } else {
    setFormErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[name];
      return newErrors;
    });
  }
};


  const handleRemoveFile = () => {
    setFormValues((prev) => ({
      ...prev,
      comprovantivoHabilitacoes: null,
    }));

    if (formErrors.comprovantivoHabilitacoes) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.comprovantivoHabilitacoes;
        return newErrors;
      });
    }
  };

 const validateForm = (): Record<string, string> => {
  const errors: Record<string, string> = {};

  if (!formValues.inscricaoTipo) {
    errors.inscricaoTipo = "Campo obrigatório";
  }

  if (
    (formValues.inscricaoTipo === "entidades_publicas" ||
      formValues.inscricaoTipo === "entidades_gestoras") &&
    !formValues.entidade
  ) {
    errors.entidade = "Campo obrigatório";
  }

  if (
    (formValues.inscricaoTipo === "municipio" ||
      formValues.inscricaoTipo === "varios_municipios") &&
    !formValues.nomeAdministrador
  ) {
    errors.nomeAdministrador = "Campo obrigatório";
  }

  if (!formValues.nomeCompleto) {
    errors.nomeCompleto = "Campo obrigatório";
  }

  if (!formValues.emailInstitucional) {
    errors.emailInstitucional = "Campo obrigatório";
  } else {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formValues.emailInstitucional)) {
      errors.emailInstitucional = "Email inválido";
    }
  }

  if (formValues.emailRequerente && formValues.emailRequerente.trim() !== "") {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formValues.emailRequerente)) {
      errors.emailRequerente = "Email inválido";
    }
  }

  if (!formValues.telefone) {
    errors.telefone = "Campo obrigatório";
  } else {
    const numbersOnly = formValues.telefone.replace(/\D/g, "");
    if (numbersOnly.length < 9) {
      errors.telefone = "Telefone deve ter no mínimo 9 dígitos";
    }
  }

  if (!formValues.cartaoCidadao) {
    errors.cartaoCidadao = "Campo obrigatório";
  } else {
    const digitsOnly = formValues.cartaoCidadao.replace(/[\s-]/g, "");
    if (digitsOnly.length < 11) {
      errors.cartaoCidadao = "Cartão de cidadão deve ter no mínimo 11 dígitos";
    }
  }

  if (!formValues.comprovantivoHabilitacoes) {
    errors.comprovantivoHabilitacoes = "Deve anexar o comprovativo de habilitações";
  }

  if (!formValues.habilitacoes) {
    errors.habilitacoes = "Campo obrigatório";
  }

  if (!formValues.declaracaoRequerente) {
    errors.declaracaoRequerente = "Deve aceitar a declaração";
  }

  if (!formValues.declaracaoResponsabilidade) {
    errors.declaracaoResponsabilidade = "Deve aceitar a declaração de responsabilidade";
  }
  if (!formValues.declaracaoPrivacidade) {
    errors.declaracaoPrivacidade = "Deve aceitar Termos e Condições de Uso Aceitável da Plataforma BUPi Backoffice";
  }

  return errors;
};

  const isFormValid = (): boolean => {
  const errors = validateForm();
  return Object.keys(errors).length === 0 && 
         Boolean(formValues.declaracaoRequerente) && 
         Boolean(formValues.declaracaoResponsabilidade) && 
         Boolean(formValues.declaracaoPrivacidade);
};

  const sendEmailNotification = async (formData: FormValues) => {
    try {
      const sanitizedData = {
        inscricaoTipo: sanitizeInput(formData.inscricaoTipo),
        nomeCompleto: sanitizeInput(formData.nomeCompleto),
        emailInstitucional: sanitizeEmail(formData.emailInstitucional),
        telefone: sanitizePhone(formData.telefone),
        cartaoCidadao: sanitizeInput(formData.cartaoCidadao),
        habilitacoes: sanitizeInput(formData.habilitacoes),
        emailRequerente: formData.emailRequerente
          ? sanitizeEmail(formData.emailRequerente)
          : "",
        entidade: sanitizeInput(formData.entidade || ""),
        municipio: sanitizeInput(formData.municipio || ""),
        municipiosAderentes: sanitizeInput(formData.municipiosAderentes || ""),
        nomeAdministrador: sanitizeInput(formData.nomeAdministrador || ""),
      };

      const tipoTraduzido = formData.inscricaoTipo
        ? t(`fields.inscricaoTipo.${formData.inscricaoTipo}`)
        : "";
      const habilitacoesTraduzidas = formData.habilitacoes
        ? t(`fields.habilitacoes.${formData.habilitacoes}`)
        : "";
      const entidadeTraduzida = formData.entidade
        ? t(`fields.entidade.options.${formData.entidade}`)
        : "";

      const emailContent =
        `<p><b>DADOS DA INSCRIÇÃO:</b></p>` +
        `<p>Tipo Inscrição: ${tipoTraduzido}</p>` +
        (sanitizedData.entidade
          ? `<p>Entidade: ${entidadeTraduzida}</p>`
          : "") +
        (sanitizedData.municipio
          ? `<p>Município: ${sanitizedData.municipio}</p>`
          : "") +
        (sanitizedData.municipiosAderentes
          ? `<p>Município Aderente: ${sanitizedData.municipiosAderentes}</p>`
          : "") +
        (sanitizedData.nomeAdministrador
          ? `<p>Nome técnico administrador: ${sanitizedData.nomeAdministrador}</p>`
          : "") +
        `<br>` +
        `<p><b>DADOS PESSOAIS:</b></p>` +
        `<p>Nome: ${sanitizedData.nomeCompleto}</p>` +
        `<p>Email: ${sanitizedData.emailInstitucional}</p>` +
        `<p>Telefone: ${sanitizedData.telefone}</p>` +
        `<p>Nº Cartão de Cidadão: ${sanitizedData.cartaoCidadao}</p>` +
        `<p>Habilitações: ${habilitacoesTraduzidas}</p>` +
        (sanitizedData.emailRequerente
          ? `<p>Email Requerente: ${sanitizedData.emailRequerente}</p>`
          : "") +
        `<br>` +
        `<p>Este email foi gerado automaticamente pelo sistema BUPI.</p>`;

      const completeHtml = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta http-equiv="Content-Type" content="text/html; charset=UTF-8">
</head>
<body style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
  ${emailContent}
</body>
</html>
    `;
      const encoder = new TextEncoder();
      const utf8Bytes = encoder.encode(completeHtml);
      const base64Data = btoa(String.fromCharCode(...utf8Bytes));

      const emailFormData = new FormData();
      emailFormData.append("to", `${process.env.NEXT_PUBLIC_BUPI_EMAIL_TO}`);
      emailFormData.append("subject", "Nova Inscrição - Técnico Habilitado");
      emailFormData.append("html", base64Data);

      if (formData.comprovantivoHabilitacoes) {
        emailFormData.append("file", formData.comprovantivoHabilitacoes);
      }

      const response = await fetch("/api/send-email", {
        method: "POST",
        body: emailFormData,
      });

      if (!response.ok) {
        throw new Error("Erro ao enviar email");
      }
    } catch (error) {
      console.error("Erro ao processar dados:", error);
      throw error;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsSubmitting(true);

    try {
      await sendEmailNotification(formValues);
      setSubmitStatus("success");

      const initialValues: FormValues = {
        inscricaoTipo: "",
        entidade: "",
        municipio: "",
        municipiosAderentes: "",
        nomeAdministrador: "",
        nomeCompleto: "",
        emailInstitucional: "",
        telefone: "",
        cartaoCidadao: "",
        habilitacoes: "",
        emailRequerente: "",
        declaracaoRequerente: false,
        declaracaoResponsabilidade: false,
        declaracaoPrivacidade: false,
      };
      setFormValues(initialValues);
      setFormErrors({});
    } catch (error) {
      console.error("Erro ao enviar formulário:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const FORM_DATA = {
    inscricaoTipoOptions: [
      { value: "municipio", key: "municipio" },
      { value: "varios_municipios", key: "varios_municipios" },
      { value: "entidades_publicas", key: "entidades_publicas" },
      { value: "entidades_gestoras", key: "entidades_gestoras" },
      //{ value: "comissao_coordenacao", key: "comissao_coordenacao" },
      { value: "tecnicos_cadastro", key: "tecnicos_cadastro" },
    ],
    entidadeOptions: {
      entidades_gestoras: [
        { value: "aigps_oigps_zifs", key: "aigps_oigps_zifs" },
        {
          value: "organizacao_agricultores_produtores_florestais",
          key: "organizacao_agricultores_produtores_florestais",
        },
        { value: "entidades_expropriantes", key: "entidades_expropriantes" },
      ],
      entidades_publicas: [
        { value: "agif", key: "agif" },
        { value: "apa", key: "apa" },
        { value: "anepc", key: "anepc" },
        { value: "ccdr", key: "ccdr" },
        { value: "dgadr", key: "dgadr" },
        { value: "dgt", key: "dgt" },
        { value: "gnr", key: "gnr" },
        { value: "infraestruturas_portugal", key: "infraestruturas_portugal" },
        { value: "icnf", key: "icnf" },
        { value: "ifap", key: "ifap" },
        { value: "igfss", key: "igfss" },
        { value: "municipios_sgifr", key: "municipios_sgifr" },
        { value: "srf_drp", key: "srf_drp" },
        { value: "estamo", key: "estamo" },
        { value: "freguesia", key: "freguesia" },
        { value: "draac", key: "draac" },
        { value: "drot", key: "drot" },
      ],
    } as Record<string, { value: string; key: string }[]>,
    habilitacoesOptions: [
      { value: "habilitacao_superior", key: "habilitacao_superior" },
      { value: "curso_tecnologico", key: "curso_tecnologico" },
      { value: "habilitado_lei_2015", key: "habilitado_lei_2015" }
    ],
  };

  const municipios =
    servicesAvailable?.publicationsData?.docs
      ?.map((doc: { municipality: string }) => ({
        value: doc.municipality.toLowerCase().replace(/\s+/g, "-"),
        label: doc.municipality,
      }))
      .sort((a: { label: string }, b: { label: any }) =>
        a.label.localeCompare(b.label)
      ) || [];

  const municipiosAderentes =
    servicesAvailable?.publicationsData?.docs
      ?.filter((doc: { bupi: boolean }) => doc.bupi === true)
      ?.map((doc: { municipality: string }) => ({
        value: doc.municipality.toLowerCase().replace(/\s+/g, "-"),
        label: doc.municipality,
      }))
      .sort((a: { label: string }, b: { label: any }) =>
        a.label.localeCompare(b.label)
      ) || [];

  const entidadeOptions = FORM_DATA.entidadeOptions[formValues.inscricaoTipo] || [];

  const ref = useRef(null);

  const controls = useAnimation();
  const isInView = useInView(ref, { once: true, amount: 0.3 });

  useEffect(() => {
    if (isInView) {
      controls.start({
        backgroundSize: "100% 50%",
        transition: { duration: 1.5, ease: "easeInOut" },
      });
    }
  }, [isInView, controls]);

  useEffect(() => {
    if (submitStatus === "success" || submitStatus === "error") {
      statusMessageRef.current?.focus();
    }
  }, [submitStatus]);

  return (
    <div className="xl:container mx-auto w-full mb-128">
      <Breadcrumb
        items={[
          {
            label: translate("Breadcrumb.homePage"),
            url: "/",
          },
          {
            label: translate("Breadcrumb.formsRegistration"),
            url: "#",
          },
        ]}
      />

      <h2 className="mt-32 xl:mt-64 xl:mb-96 md:mt-[44px] md:mb-[64px]">
        <motion.div
          ref={ref}
          className="text-xl-regular md:text-2xl-regular md:max-w-[90%] xl:text-3xl-regular text-neutral-900 bg-no-repeat bg-top  whitespace-pre-line"
          style={{
            display: "inline",
            boxDecorationBreak: "clone",
            WebkitBoxDecorationBreak: "clone",
            backgroundImage: "linear-gradient(to bottom, #F1F6F3, #F1F6F3)",
          }}
          initial={{ backgroundSize: "0% 50%" }}
          animate={controls}
        >
          {t("headingtitle")} <span className="font-bold text-xl-bold md:text-2xl-bold md:max-w-[90%] xl:text-3xl-bold text-neutral-900">{t("headingtitleBold")}</span>
        </motion.div>
      </h2>

      <div className="xl:container mx-auto xl:max-w-3xl">
        <div className="mb-16">
          <h2 className="text-xl-bold text-neutral-900"> {t("title")} </h2>
          <p className="my-32 text-m-regular">{t("description")}</p>

          <div className="">
            <p className="text-neutral-900 mb-32 text-m-regular">
              {t("legal.article3.title")}
            </p>
            <p className="text-neutral-900 mb-32 text-m-regular">
              {t("legal.article3.subtitle")}
            </p>
            <p className="text-neutral-900 text-m-regular">
              {t("legal.article3.subtitle2")}
            </p>
          </div>
        </div>

        <div className=" mt-64">
          <h2 className="text-xl-bold text-neutral-900 mb-32">
            {t("sectionTitle")}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-8">
            <p className="mb-32 text-danger-500">
              <span className="text-[24px]" aria-hidden="true">*</span>
              <span className="text-s-regular"> {t("requiredFieldNote")}</span>
            </p>

            <fieldset
              className="border-0 p-0 m-0"
              aria-describedby={getDescribedBy("inscricaoTipo", "register-inscricaoTipo-helper")}
              aria-invalid={!!formErrors.inscricaoTipo}
            >
              <legend className="text-neutral-900 text-m-regular">
                <span className="text-danger-500 text-[24px]">*</span>
                {t("fields.inscricaoTipo.label")}
              </legend>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {FORM_DATA.inscricaoTipoOptions.map((option, index) => (
                  <div
                    key={option.value}
                    className={`flex items-start ${
                      index === 3
                        ? "md:col-span-3"
                        : index === 4
                        ? "md:col-span-3"
                        : ""
                    }`}
                  >
                    <RadioButton
                      label={t(`fields.inscricaoTipo.${option.key}`)}
                      value={option.value}
                      checked={formValues.inscricaoTipo === option.value}
                      aria-describedby={getDescribedBy("inscricaoTipo")}
                      aria-invalid={!!formErrors.inscricaoTipo}
                      onChange={(checked) => {
                        if (checked) {
                          handleFieldChange("inscricaoTipo", option.value);
                        }
                      }}
                    />
                  </div>
                ))}
              </div>
              <p id="register-inscricaoTipo-helper" className="mt-2 text-s-regular text-[#64718B]">
                {t("fields.inscricaoTipo.helper")}
              </p>
              {formErrors.inscricaoTipo && (
                <p id={getErrorId("inscricaoTipo")} className="mt-1 text-s-regular text-danger-500">
                  {formErrors.inscricaoTipo}
                </p>
              )}
            </fieldset>

            {entidadeOptions.length > 0 && (
              <div className="pt-32">
                <InputSelect
                  id="entidade"
                  label={
                    (
                      <>
                        <span className="text-danger-500 text-[24px]">*</span>
                        {t("fields.entidade.label")}
                      </>
                    ) as any
                  }
                  placeholder={t("fields.entidade.placeholder")}
                  dropdownAriaLabel={t("fields.entidade.dropdownAriaLabel")}
                  hasError={!!formErrors.entidade}
                  aria-describedby={getDescribedBy("entidade")}
                  aria-invalid={!!formErrors.entidade}
                  onChange={(selected) => {
                    const value = selected.length > 0 ? selected[0].value : "";
                    handleFieldChange("entidade", value);
                  }}
                >
                  <DropdownSection
                    className={`${entidadeOptions.length > 4 ? "h-[300px]" : ""}`}
                    name="entidade"
                  >
                    {entidadeOptions.map((option) => (
                      <DropdownOption
                        key={option.value}
                        value={option.value}
                        selected={formValues.entidade === option.value}
                      >
                        {t(`fields.entidade.options.${option.key}`)}
                      </DropdownOption>
                    ))}
                  </DropdownSection>
                </InputSelect>
                {formErrors.entidade && (
                  <p id={getErrorId("entidade")} className="mt-1 text-s-regular text-danger-500">
                    {formErrors.entidade}
                  </p>
                )}
              </div>
            )}

            {(formValues.inscricaoTipo === "municipio" ||
              formValues.inscricaoTipo === "varios_municipios") && (
              <div className="pt-32 grid grid-cols-1 md:grid-cols-2 mt-32 gap-x-16 gap-y-32">
                <div>
                  <InputSelect
                    id="municipio"
                    label={t("fields.municipio.label")}
                    placeholder={t("fields.municipio.placeholder")}
                    className="text-neutral-900"
                    dropdownAriaLabel="Lista de municípios"
                    hasError={!!formErrors.municipio}
                    aria-describedby={getDescribedBy("municipio")}
                    aria-invalid={!!formErrors.municipio}
                    onChange={(selected) => {
                      const value =
                        selected.length > 0 ? selected[0].value : "";
                      handleFieldChange("municipio", value);
                    }}
                  >
                    <DropdownSection
                      className={`${
                        municipios.length > 4
                          ? "h-[300px]"
                          : `h-[${municipios.length * 1}px]`
                      }`}
                      name="municipio"
                    >
                      {municipios.map(
                        (
                          municipio: { value: string; label: string },
                          index: number
                        ) => (
                          <DropdownOption
                            key={`municipio-${index}`}
                            value={municipio.value}
                            selected={formValues.municipio === municipio.value}
                          >
                            {municipio.label}
                          </DropdownOption>
                        )
                      )}
                    </DropdownSection>
                  </InputSelect>
                  {formErrors.municipio && (
                    <p id={getErrorId("municipio")} className="mt-1 text-s-regular text-danger-500">
                      {formErrors.municipio}
                    </p>
                  )}
                </div>
                <div>
                  <InputSelect
                    id="municipiosAderentes"
                    label={t("fields.municipiosAderentes.label")}
                    placeholder={t("fields.municipiosAderentes.placeholder")}
                    dropdownAriaLabel="Lista de municípios aderentes"
                    hasError={!!formErrors.municipiosAderentes}
                    aria-describedby={getDescribedBy("municipiosAderentes")}
                    aria-invalid={!!formErrors.municipiosAderentes}
                    onChange={(selected) => {
                      const value =
                        selected.length > 0 ? selected[0].value : "";
                      handleFieldChange("municipiosAderentes", value);
                    }}
                  >
                    <DropdownSection
                      className={`${
                        municipiosAderentes.length > 4
                          ? "h-[300px]"
                          : `h-[${municipiosAderentes.length * 1}px]`
                      }`}
                      name="municipiosAderentes"
                    >
                      {municipiosAderentes.map(
                        (
                          municipio: { value: string; label: string },
                          index: number
                        ) => (
                          <DropdownOption
                            key={`municipio-aderente-${index}`}
                            value={municipio.value}
                            selected={
                              formValues.municipiosAderentes === municipio.value
                            }
                          >
                            {municipio.label}
                          </DropdownOption>
                        )
                      )}
                    </DropdownSection>
                  </InputSelect>
                  {formErrors.municipiosAderentes && (
                    <p id={getErrorId("municipiosAderentes")} className="mt-1 text-s-regular text-danger-500">
                      {formErrors.municipiosAderentes}
                    </p>
                  )}
                </div>
              </div>
            )}

            {(formValues.inscricaoTipo === "municipio" ||
              formValues.inscricaoTipo === "varios_municipios") && (
              <div className="pt-32">
                <InputText
                  id="nomeAdministrador"
                  //label={t("fields.nomeAdministrador.label")}
                  label={
                    (
                      <>
                        <span className="text-danger-500 text-[24px]">*</span>
                        {t("fields.nomeAdministrador.label")}
                      </>
                    ) as any
                  }
                  required
                  value={formValues.nomeAdministrador || ""}
                  onChange={(e) =>
                    handleFieldChange("nomeAdministrador", e.target.value)
                  }
                  placeholder={t("fields.nomeAdministrador.placeholder")}
                  hasError={!!formErrors.nomeAdministrador}
                  aria-describedby={getDescribedBy("nomeAdministrador")}
                  aria-invalid={!!formErrors.nomeAdministrador}
                />
                {formErrors.nomeAdministrador && (
                  <p id={getErrorId("nomeAdministrador")} className="mt-1 text-s-regular text-danger-500">
                    {formErrors.nomeAdministrador}
                  </p>
                )}
              </div>
            )}

            <div className="pt-32 grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-32">
              <div>
                <InputText
                  id="nomeCompleto"
                  label={
                    (
                      <>
                        <span className="text-danger-500 text-[24px]">*</span>
                        {t("fields.nomeCompleto.label")}
                      </>
                    ) as any
                  }
                  required
                  value={formValues.nomeCompleto || ""}
                  onChange={(e) =>
                    handleFieldChange("nomeCompleto", e.target.value)
                  }
                  placeholder={t("fields.nomeCompleto.placeholder")}
                  hasError={!!formErrors.nomeCompleto}
                  aria-describedby={getDescribedBy("nomeCompleto")}
                  aria-invalid={!!formErrors.nomeCompleto}
                />
                {formErrors.nomeCompleto && (
                  <p id={getErrorId("nomeCompleto")} className="mt-1 text-s-regular text-danger-500">
                    {formErrors.nomeCompleto}
                  </p>
                )}
              </div>

              <div>
                <InputText
                  id="emailInstitucional"
                  label={
                    (
                      <>
                        <span className="text-danger-500 text-[24px]">*</span>
                        {t("fields.emailInstitucional.label")}
                      </>
                    ) as any
                  }
                  required
                  type="email"
                  value={formValues.emailInstitucional || ""}
                  onChange={(e) =>
                    handleFieldChange("emailInstitucional", e.target.value)
                  }
                  placeholder={t("fields.emailInstitucional.placeholder")}
                  hasError={!!formErrors.emailInstitucional}
                  aria-describedby={getDescribedBy("emailInstitucional")}
                  aria-invalid={!!formErrors.emailInstitucional}
                />
                {formErrors.emailInstitucional && (
                  <p id={getErrorId("emailInstitucional")} className="mt-1 text-s-regular text-danger-500">
                    {formErrors.emailInstitucional}
                  </p>
                )}
              </div>

              <div>
                <InputText
                  id="telefone"
                  label={
                    (
                      <>
                        <span className="text-danger-500 text-[24px]">*</span>
                        {t("fields.telefone.label")}
                      </>
                    ) as any
                  }
                  required
                  type="tel"
                  value={formValues.telefone || ""}
                  onChange={(e) => {
                    const numbersOnly = e.target.value.replace(/\D/g, "");
                    handleFieldChange("telefone", numbersOnly);
                  }}
                  placeholder={t("fields.telefone.placeholder")}
                  hasError={!!formErrors.telefone}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  aria-describedby={getDescribedBy("telefone")}
                  aria-invalid={!!formErrors.telefone}
                />
                 {formErrors.telefone && (
                  <p id={getErrorId("telefone")} className="mt-1 text-s-regular text-danger-500">
                    {formErrors.telefone}
                  </p>
                )}
              </div>

              <div>
                <InputText
                  id="cartaoCidadao"
                  label={
                    (
                      <>
                        <span className="text-danger-500 text-[24px]">*</span>
                        {t("fields.cartaoCidadao.label")} <span className="text-neutral-900 text-s-regular"> (XXXXXXX-XXXX) </span>
                      </>
                    ) as any
                  }
                  required
                  value={formValues.cartaoCidadao || ""}
                  onChange={(e) =>
                    handleFieldChange("cartaoCidadao", e.target.value)
                  }
                  placeholder={t("fields.cartaoCidadao.placeholder")}
                  hasError={!!formErrors.cartaoCidadao}
                  aria-describedby={getDescribedBy("cartaoCidadao")}
                  aria-invalid={!!formErrors.cartaoCidadao}
                />
                {formErrors.cartaoCidadao && (
                  <p id={getErrorId("cartaoCidadao")} className="mt-1 text-s-regular text-danger-500">
                    {formErrors.cartaoCidadao}
                  </p>
                )}
              </div>
            </div>

            <div className="pt-32">
              <InputSelect
                id="habilitacoes"
                label={
                  (
                    <>
                      <span className="text-danger-500 text-[24px]">*</span>
                      {t("fields.habilitacoes.label")}
                    </>
                  ) as any
                }
                placeholder={t("fields.habilitacoes.placeholder")}
                dropdownAriaLabel="Lista de habilitações"
                hasError={!!formErrors.habilitacoes}
                aria-describedby={getDescribedBy("habilitacoes")}
                aria-invalid={!!formErrors.habilitacoes}
                onChange={(selected) => {
                  const value = selected.length > 0 ? selected[0].value : "";
                  handleFieldChange("habilitacoes", value);
                }}
              >
                <DropdownSection name="habilitacoes">
                  {FORM_DATA.habilitacoesOptions.map((option) => (
                    <DropdownOption
                      key={option.value}
                      value={option.value}
                      selected={formValues.habilitacoes === option.value}
                    >
                      {t(`fields.habilitacoes.${option.key}`)}
                    </DropdownOption>
                  ))}
                </DropdownSection>
              </InputSelect>
              {formErrors.habilitacoes && (
                <p id={getErrorId("habilitacoes")} className="mt-1 text-s-regular text-danger-500">
                  {formErrors.habilitacoes}
                </p>
              )}
            </div>

            <div className="pt-32">
              <label htmlFor="comprovativo-upload" className="block text-m-medium text-neutral-900 mb-8">
                <span className="text-danger-500 text-[24px]">*</span>
                {t("fields.comprovantivoHabilitacoes.label")}
              </label>
              <div
                className={`bg-primary-100 w-full p-16 ${
                  formErrors.comprovantivoHabilitacoes
                    ? "border-2 border-red-500"
                    : ""
                }`}
              >
                <input
                  type="file"
                  accept=".pdf"
                  required
                  className="hidden"
                  id="comprovativo-upload"
                  aria-describedby={getDescribedBy("comprovantivoHabilitacoes", "register-comprovantivoHabilitacoes-helper")}
                  aria-invalid={!!formErrors.comprovantivoHabilitacoes}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      handleFieldChange("comprovantivoHabilitacoes", file);
                    }
                  }}
                />

                {!formValues.comprovantivoHabilitacoes ? (
                  <div className="text-center">
                    <button
                      type="button"
                      className="w-full flex justify-center gap-8 bg-primary-600 text-m-semibold border-0 p-16 text-white"
                      aria-describedby={getDescribedBy("comprovantivoHabilitacoes", "register-comprovantivoHabilitacoes-helper")}
                      aria-invalid={!!formErrors.comprovantivoHabilitacoes}
                      onClick={() =>
                        document.getElementById("comprovativo-upload")?.click()
                      }
                    >
                      {t("fields.comprovantivoHabilitacoes.buttonText")}
                      <Icon title="Upload" name="agora-line-upload" className="fill-white" />
                    </button>
                    <p id="register-comprovantivoHabilitacoes-helper" className="mt-16 text-s-regular text-neutral-900">
                      {t("fields.comprovantivoHabilitacoes.helper")}
                    </p>
                  </div>
                ) : (
                  <div className="flex items-center justify-between bg-white p-12 rounded border">
                    <div className="flex items-center gap-8 min-w-0 flex-1">
                      <Icon
                        name="agora-line-file"
                        className="fill-neutral-700 flex-shrink-0"
                      />
                      <span className="text-m-regular line-clamp-1 text-neutral-900 overflow-hidden text-ellipsis min-w-0">
                        {formValues.comprovantivoHabilitacoes.name}
                      </span>
                    </div>
                    <div className="flex gap-8 flex-shrink-0">
                      <button
                        type="button"
                        className="p-8 text-danger-500 hover:text-danger-700"
                        onClick={handleRemoveFile}
                        title="Remover ficheiro"
                      >
                        <Icon
                          name="agora-solid-x"
                          className="fill-neutral-700 flex-shrink-0"
                        />
                      </button>
                    </div>
                  </div>
                )}
              </div>
              {formErrors.comprovantivoHabilitacoes && (
                <p id={getErrorId("comprovantivoHabilitacoes")} className="mt-8 text-s-regular text-danger-500">
                  {formErrors.comprovantivoHabilitacoes}
                </p>
              )}
            </div>

            <div className="pt-32">
              <InputText
                id="emailRequerente"
                label={t("fields.emailRequerente.label")}
                type="email"
                required={false}
                value={formValues.emailRequerente || ""}
                onChange={(e) =>
                  handleFieldChange("emailRequerente", e.target.value)
                }
                placeholder={t("fields.emailRequerente.placeholder")}
                hasError={!!formErrors.emailRequerente}
                aria-describedby={getDescribedBy("emailRequerente", "register-emailRequerente-helper")}
                aria-invalid={!!formErrors.emailRequerente}
              />
              {formErrors.emailRequerente && (
                  <p id={getErrorId("emailRequerente")} className="mt-1 text-s-regular text-danger-500">
                    {formErrors.emailRequerente}
                  </p>
                )}
              <p id="register-emailRequerente-helper" className="text-s-regular text-[#64718B] mt-8">
                {t("fields.emailRequerente.helper")}
              </p>
            </div>

            <div className="pt-32 space-y-32">
              <fieldset
                className="border-0 p-0 m-0"
                aria-describedby={getDescribedBy("declaracaoRequerente")}
                aria-invalid={!!formErrors.declaracaoRequerente}
              >
                <legend className="block text-s-regular font-medium text-gray-700 mb-3">
                  <span className="text-danger-500 text-[24px]">*</span>
                  {t("fields.declaracaoRequerente.label")}
                </legend>
                <Checkbox
                  label={t("fields.declaracaoRequerente.text")}
                  checked={Boolean(formValues.declaracaoRequerente)}
                  aria-describedby={getDescribedBy("declaracaoRequerente")}
                  aria-invalid={!!formErrors.declaracaoRequerente}
                  onChange={(checked) =>
                    handleFieldChange("declaracaoRequerente", checked)
                  }
                />
                {formErrors.declaracaoRequerente && (
                  <p id={getErrorId("declaracaoRequerente")} className="mt-1 text-s-regular text-danger-500">
                    {formErrors.declaracaoRequerente}
                  </p>
                )}
              </fieldset>

              <fieldset
                className="border-0 p-0 m-0"
                aria-describedby={getDescribedBy("declaracaoResponsabilidade")}
                aria-invalid={!!formErrors.declaracaoResponsabilidade}
              >
                <legend className="block text-s-regular font-medium text-neutral-900 mb-3">
                  <span className="text-danger-500 text-[24px]">*</span>
                  {t("fields.declaracaoResponsabilidade.label")}
                </legend>
                <Checkbox
                  label={t("fields.declaracaoResponsabilidade.text")}
                  checked={Boolean(formValues.declaracaoResponsabilidade)}
                  aria-describedby={getDescribedBy("declaracaoResponsabilidade")}
                  aria-invalid={!!formErrors.declaracaoResponsabilidade}
                  onChange={(checked) =>
                    handleFieldChange("declaracaoResponsabilidade", checked)
                  }
                />
                {formErrors.declaracaoResponsabilidade && (
                  <p id={getErrorId("declaracaoResponsabilidade")} className="mt-1 text-s-regular text-danger-500">
                    {formErrors.declaracaoResponsabilidade}
                  </p>
                )}
              </fieldset>

              <fieldset
                className="border-0 p-0 m-0"
                aria-describedby={getDescribedBy("declaracaoPrivacidade")}
                aria-invalid={!!formErrors.declaracaoPrivacidade}
              >
                <legend className="block text-s-regular font-medium text-neutral-900 mb-3">
                  <span className="text-danger-500 text-[24px]">*</span>
                  Termos e Condições de Uso Aceitável da Plataforma BUPi Backoffice
                </legend>
                <Checkbox
                  label={(<>
                    <span>
                      Este documento estabelece os termos e condições de uso aceitável da Plataforma BUPi Backoffice, garantindo a segurança, a legalidade e a rastreabilidade das ações efetuadas na plataforma.{" "}
                      
                      .
                    </span></>) as any
                  }
                  checked={Boolean(formValues.declaracaoPrivacidade)}
                  aria-describedby={getDescribedBy("declaracaoPrivacidade")}
                  aria-invalid={!!formErrors.declaracaoPrivacidade}
                  onChange={(checked) =>
                    handleFieldChange("declaracaoPrivacidade", checked)
                  }
                />
                <a 
                        href="https://sta-informativo.bupi.gov.pt/media/31_SGSI.DOC.TCUA.V1.1-Termos_e_Condic%CC%A7o%CC%83es_de_Uso_Aceita%CC%81vel_da_Plataforma_BUPi_Backoffice.pdf" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary-600 underline hover:text-primary-700"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Consulte aqui
                      </a>
                {formErrors.declaracaoPrivacidade && (
                  <p id={getErrorId("declaracaoPrivacidade")} className="mt-1 text-s-regular text-danger-500">
                    {formErrors.declaracaoPrivacidade}
                  </p>
                )}
              </fieldset>
            </div>

            <div className="pt-32">
              <Button
                type="submit"
                disabled={isSubmitting || !isFormValid()}
                className={`${
                  !isFormValid() ? "opacity-50 cursor-not-allowed" : ""
                } w-full md:w-auto`}
                hasIcon
                appearance="solid"
                trailingIcon="agora-line-arrow-right-circle"
                trailingIconHover="agora-solid-arrow-right-circle"
              >
                {isSubmitting ? t("submitting") : t("submitButton")}
              </Button>

              <div
                ref={statusMessageRef}
                tabIndex={-1}
                className="focus:outline-none"
              >
                <div aria-live="polite" aria-atomic="true" role="status">
                  {submitStatus === "success" && (
                    <div className="mt-16">
                      <p className="text-primary-500">{t("messages.success")}</p>
                    </div>
                  )}
                </div>

                <div aria-live="assertive" aria-atomic="true" role="alert">
                  {submitStatus === "error" && (
                    <div className="mt-4 p-4 bg-danger-50 border border-danger-200 rounded-md">
                      <p className="text-danger-800">{t("messages.error")}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </form>

          <div className="mt-64">
            <p className="mt-6 mb-16 text-s-bold text-primary-500">
              {t("legal.article3.title")}
            </p>
            <p className="mt-2 text-s-regular text-neutral-900 whitespace-pre-line">
              {t("legal.article3.text")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
